// Packages
import throttle from 'underscore/modules/throttle.js';

/**
 * This manages all of the scenes registered to it and fires off the
 * appropriate callbacks for each scene depending on scroll position.
 */
export class ScrollController {
  constructor() {
    this.sceneRegistry = [];
    this.sceneIdCounter = 0;
    this.windowHeight = window.innerHeight;
    this.running = false;

    window.addEventListener('resize', throttle(() => {
      this.updateSceneData();
    }, 100));

    window.addEventListener('scroll-scene-resized', this.updateSceneData);
  }

  start() {
    if (!this.running) {
      this.running = true;
      this.animationLoop();
    }
  }

  stop() {
    this.running = false;
  }

  animationLoop() {
    if (this.running && this.sceneRegistry.length > 0) {
      requestAnimationFrame(() => {
        this.handleScrollUpdates();
      });
    }
  }

  // Update values for each in sceneRegistry
  updateSceneData = () => {
    this.windowHeight = window.innerHeight;
    this.sceneRegistry.forEach((registered) => {
      registered.data = registered.scene.getData();
    });
  };

  // Add scene(s) to the ScrollController.
  // Accepts a single scene or array of multiple scenes.
  addScene(sceneInput) {
    const scenes = sceneInput.length ? sceneInput : [sceneInput];

    const newRegistrations = scenes.map((scene) => {
      // The data we retrieve from ScrollScene.getData() provides the
      // controller with the bounds of the scene.
      const sceneData = {
        scene: scene,
        data: scene.getData(),
        id: this.generateSceneId(),
      };

      // TODO what if someone pushes an already existing scene?
      this.sceneRegistry.push(sceneData);
      return sceneData;
    });

    this.start();

    return newRegistrations;
  }

  removeScene(sceneInput) {
    const scenes = sceneInput.length ? sceneInput : [sceneInput];
    const sceneIds = scenes.map((s) => s.id);
    const remainingScenes = [];
    const deletedScenes = [];

    this.sceneRegistry.forEach((registeredScene) => {
      if (sceneIds.includes(registeredScene.id)) {
        deletedScenes.push(registeredScene);
      } else {
        remainingScenes.push(registeredScene);
      }
    });

    this.sceneRegistry = remainingScenes;
    return deletedScenes;
  }

  generateSceneId() {
    this.sceneIdCounter += 1;
    return this.sceneIdCounter;
  }

  // Run this function on each requestAnimationFrame to update with the
  // provided scroll behavior for the user's position on the page.
  handleScrollUpdates() {
    let currentScroll = window.scrollY;

    // Clamp currentScroll to 0 minimum to make Safari and Chrome more consistent
    if (currentScroll < 0) currentScroll = 0;

    this.sceneRegistry.forEach((registered) => {
      const { data, scene } = registered;

      // Where the scene is currently relative to the registered scene
      const scrollProgress = (currentScroll - data.startPoint) / data.distance;

      // Clamp progress to [0,1] to prevent from firing too often
      const clampedProgress =
        scrollProgress < 0 ? 0 : scrollProgress > 1 ? 1 : scrollProgress;

      // Apply the easing function
      const easedProgress = scene.ease(clampedProgress);

      if (
        currentScroll >= data.topLoadPoint &&
        currentScroll <= data.bottomLoadPoint
      ) {
        scene._onLoad(easedProgress);
      } else {
        scene._onUnload(easedProgress);
      }

      scene._onProgress(easedProgress);

      // If the element is onscreen
      if (
        currentScroll >= data.visibilityPoint &&
        currentScroll <= data.bottom
      ) {
        scene._onElementEnter(easedProgress);

        // If scene is in progress
        if (
          currentScroll >= data.startPoint &&
          currentScroll <= data.endPoint
        ) {
          scene._onSceneBegin(easedProgress);
        }
      } else {
        // If the element leaves the viewport
        scene._onElementLeave(easedProgress);
      }

      // If outside bounds of scene
      if (currentScroll < data.startPoint || currentScroll > data.endPoint) {
        scene._onSceneEnd(easedProgress);
      }
    });

    this.animationLoop();
  }
}
